/**
 * Shifts API Endpoints
 *
 * A shift is a NURSE'S WORK PERIOD. Patients attach to it as assignments (one
 * shift, a patient load), so a nurse cares for many patients within a single
 * shift. A handover moves one patient from the outgoing nurse's assignment to a
 * new assignment in the incoming nurse's shift (`previous_assignment_id`).
 */

import { api } from '../client';
import type { Page } from '../types';
import { randomUUID } from '@/lib/uuid';
import { createLogger } from '@/utils/logger';

const logger = createLogger('ShiftsAPI');

/**
 * The handover pointer on an assignment created by a handover: who the
 * patient came from and the Rapport SESSION (the record itself — note,
 * dialogue and flagged conversations all live in its transcript). `from_nurse_name` is resolved server-side at read time.
 */
export interface AssignmentHandover {
  from_nurse_id: string;
  from_nurse_name?: string;
  handover_session_id?: string;
}

/** One patient's custody within a shift. */
export interface AssignmentResponse {
  id: string;
  shift_id: string;
  /** Who held the patient during this link, joined from the shift. The shift
   *  itself is readable only by that nurse, so shift_id alone cannot be
   *  resolved to a person by any other caller. */
  nurse_id: string;
  patient_id: string;
  previous_assignment_id?: string;
  started_at: string;
  ended_at?: string;
  /**
   * Whether this nurse's patient access survives the shift's `ends_at`.
   *
   * `false` = it expires with the shift. `true` = it is standing (the nurse
   * owns the patient, or holds an unconditioned grant), so revoking needs an
   * explicit handover or end. `null` = nobody worked it out on this read.
   *
   * Always on the wire, and nullable rather than optional: as an omitted
   * `false` the answer "access expires at ends_at" was the same bytes as "this
   * projection never asked".
   */
  access_standing: boolean | null;
  /** Present when this assignment was created by a handover that carried information. */
  handover?: AssignmentHandover;
  created_by: string;
}

/**
 * A nurse's work period.
 *
 * `assignments` is populated only on GET /shifts/{id}, where it is ALWAYS
 * present and `[]` means the nurse holds no patients. Everywhere else the key
 * is absent, meaning the route does not populate it. The two used to be
 * indistinguishable, so a caller could not tell "no patients" from "not asked".
 */
export interface ShiftResponse {
  id: string;
  /** The nurse. `nurse_id` is the same value under the name pre-roster clients
   *  read; the server removes it when the old shift surface goes. */
  user: string;
  nurse_id: string;
  /** The roster this shift belongs to. Required on every write. `null` only on
   *  the ad-hoc work periods that predate the roster. */
  team: string | null;
  /** The place it is worked, when the team staffs more than one. */
  location: string | null;
  /** The roster-day LABEL (YYYY-MM-DD) — NOT the period. A night shift labelled
   *  the 14th ends on the 15th, so every on-duty-at-T question compares
   *  planned_start and planned_end and never this. */
  date: string;
  /** The shift-type code, rendered as written. */
  shift_type: string;
  planned_start: string | null;
  planned_end: string | null;
  /** null until the shift starts. */
  started_at: string | null;
  /** Planned end; when set, every assignment's grant self-expires here. */
  ends_at?: string;
  /** null until it ends. Before planned_end means it ended early — that is the
   *  whole record of an early end; there is no separate state. */
  ended_at?: string;
  status: ShiftStatus;
  /** The absent shift this one covers. */
  replaces: string | null;
  /** What wrote the row: manual or legacy. */
  source: string;
  created_by: string;
  assignments?: AssignmentResponse[];
}

/**
 * The shift lifecycle. There is deliberately no `completed_early` and no
 * `no_show`: an early end is `completed` with `ended_at` before `planned_end`,
 * and a never-started planned shift is `planned` with `planned_start` in the
 * past. Both facts are already on the row.
 *
 * There is no `draft` either. An unpublished roster is a PLAN, which the
 * planning service holds; a shift row exists only once someone published the
 * plan that produced it.
 */
export type ShiftStatus =
  | 'planned'
  | 'in_progress'
  | 'completed'
  | 'cancelled'
  | 'absent';

export interface AssignPatientRequest {
  patient_id: string;
}

/**
 * One page of shifts, in the shared list envelope. Page on the response's
 * `Link: rel="next"`, never on `items.length === limit` — a filter can shrink
 * a page, which is how the same heuristic broke paging on GET /patients.
 */
export type ListShiftsResponse = Page<ShiftResponse>;

export interface ListShiftsParams {
  /** Restricts to the shift the caller is WORKING. Spelled as the AIP-160
   *  filter the server answers, so a status is a status and not a flag. */
  active?: boolean;
  /** An AIP-160 filter, e.g. `team = X AND planned_start <= "…"`. Combined with
   *  `active` above by AND. */
  filter?: string;
}

/** Fetch a single shift with its active patient assignments. */
export async function getShift(id: string): Promise<ShiftResponse> {
  try {
    logger.debug('Fetching shift', { id });
    return await api.get<ShiftResponse>(`/shifts/${id}`);
  } catch (error) {
    logger.error('Failed to fetch shift', { id, error });
    throw error;
  }
}

/** List the caller's shifts, optionally only the open one. */
export async function listShifts(
  params: ListShiftsParams = {},
): Promise<ShiftResponse[]> {
  try {
    const query = new URLSearchParams();
    const clauses: string[] = [];
    if (params.active) clauses.push('status = in_progress');
    if (params.filter) clauses.push(params.filter);
    if (clauses.length > 0) query.set('filter', clauses.join(' AND '));

    logger.debug('Listing shifts', params);
    const allShifts = await api.getAllItems<ListShiftsResponse>(`/shifts?${query.toString()}`);

    logger.info('Shifts listed', { count: allShifts.length });
    return allShifts;
  } catch (error) {
    logger.error('Failed to list shifts', { error });
    throw error;
  }
}

/** Assign a patient into the caller's own shift (cold). */
export async function assignPatient(
  shiftId: string,
  body: AssignPatientRequest,
): Promise<AssignmentResponse> {
  try {
    logger.debug('Assigning patient', { shiftId, ...body });
    const response = await api.post<AssignmentResponse>(
      `/shifts/${shiftId}/assignments`,
      body,
    );
    logger.info('Patient assigned', { id: response.id });
    return response;
  } catch (error) {
    logger.error('Failed to assign patient', { shiftId, error });
    throw error;
  }
}

/** End one patient's custody within a shift (revokes the nurse's grant). */
export async function endAssignment(
  shiftId: string,
  assignmentId: string,
): Promise<AssignmentResponse> {
  try {
    logger.debug('Ending assignment', { shiftId, assignmentId });
    return await api.post<AssignmentResponse>(
      `/shifts/${shiftId}/assignments/${assignmentId}:end`,
      {},
    );
  } catch (error) {
    logger.error('Failed to end assignment', { shiftId, assignmentId, error });
    throw error;
  }
}

/** End the whole shift (revokes every assignment's grant, then closes it). */
export async function endShift(shiftId: string): Promise<ShiftResponse> {
  try {
    logger.debug('Ending shift', { shiftId });
    return await api.post<ShiftResponse>(`/shifts/${shiftId}:end`, {});
  } catch (error) {
    logger.error('Failed to end shift', { shiftId, error });
    throw error;
  }
}

// The ward's allocation frame — GET /locations/{id}/allocation. The board's
// read: every admitted patient with current custody + nurses on open shifts.
export interface WardAllocationPatient {
  patient_id: string;
  label: string;
  /** Geographic placement — drives the board's room sections + bed ordering. */
  room?: string;
  bed?: string;
  nurse_id?: string;
  nurse_name?: string;
}

export interface WardAllocationNurse {
  nurse_id: string;
  name: string;
  patient_count: number;
}

export interface WardAllocationResponse {
  location_id: string;
  patients: WardAllocationPatient[];
  on_shift: WardAllocationNurse[];
}

/** Read the ward's allocation frame (Schichtstart board). */
export async function getWardAllocation(
  locationId: string,
): Promise<WardAllocationResponse> {
  try {
    return await api.get<WardAllocationResponse>(`/locations/${locationId}/allocation`);
  } catch (error) {
    logger.error('Failed to fetch ward allocation', { locationId, error });
    throw error;
  }
}

export interface SetPatientAssignmentRequest {
  /** The nurse who should hold the patient; must have an open shift (409 otherwise). */
  nurse_id: string;
}

/**
 * Set who holds a patient — the unit lead's allocation write
 * (PUT /patients/{id}/assignment). Unheld → cold assign; held elsewhere →
 * custody transfer via the handover saga; already there → idempotent 200.
 */
export async function setPatientAssignment(
  patientId: string,
  body: SetPatientAssignmentRequest,
): Promise<AssignmentResponse> {
  try {
    logger.debug('Setting patient assignment', { patientId, ...body });
    const response = await api.put<AssignmentResponse>(
      `/patients/${patientId}/assignment`,
      body,
      { headers: { 'Idempotency-Key': randomUUID() } },
    );
    logger.info('Patient assignment set', { id: response.id });
    return response;
  } catch (error) {
    logger.error('Failed to set patient assignment', { patientId, error });
    throw error;
  }
}
